// src/features/landing/presentation/pages/PlannerRecruitApplyPage.jsx
// 설계사 채용관 전용 "보험설계사 채용등록" (targetType = PLANNER).
// 대리점 대표/채용 담당자 폼과 달리 지사 실적·급여형 채용조건이 없고,
// 설계사 이직 판단에 필요한 4개 섹션(채용 지역 / 강점 영업 영역 / 이직 시 제공 조건 / 지원 사항)만 받는다.
// "지사 현황" 섹션은 2026-09-13 제거 — 설계사 채용관 채용등록은 더 이상 이 항목을 받지 않는다
// (대표/담당자 선택 화면도 함께 제거돼 이 폼은 항상 AGENCY_CEO로 진입한다. 기존 HR_MANAGER
// 게시글 수정만 /manager 라우트로 남아 있음).
// "이직 시 제공 조건" 섹션은 같은 날 정착지원금/근무형태 2필드 → 영업 베네핏·교육 시스템·
// 평균 소득·기본수수료+프로모션 4필드 추가로 확장됨(총 6필드). 정착지원금 라벨도
// "정착지원금 또는 교육비"로 변경.
// "고용 형태"(위촉직/계약직/정규직, 복수 선택 체크박스) 필드 2026-09-13 추가 —
// "근무 형태"(전업/겸업가능/자유(재택), 단일 선택)와는 별개 축.
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { recruitmentRequestRepository } from "../../infrastructure/recruitmentRequestApiRepository";
import { termsRepository } from "../../infrastructure/termsApiRepository";
import { useAuthContext } from "../../../auth/application/AuthContext";
import {
    RECRUIT_REGIONS,
    STRONG_SALES_AREAS,
    WORK_TYPES,
    EMPLOYMENT_TYPES,
    SUPPORT_ITEMS,
} from "../../domain/recruitmentOptions";
import "../styles/landing.css";

const formatPhone = (raw) => {
    if (!raw) return "";
    const d = raw.replace(/\D/g, "");
    if (d.startsWith("02")) {
        if (d.length <= 2) return d;
        if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
        if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
        return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
    }
    if (/^1[0-9]/.test(d)) return d.length <= 4 ? d : `${d.slice(0, 4)}-${d.slice(4)}`;
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
    if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const INITIAL_FORM = {
    contactName: "",
    contactPhone: "",
    companyName: "",
    companyAddress: "",
    companyAddressDetail: "",
    recruitRegions: [],
    strongSalesAreas: [],
    strongSalesAreasEtc: "",
    relocationSettlementSupport: "",
    relocationWorkType: "",
    employmentTypes: [],
    salesBenefit: "",
    educationSystem: "",
    averageIncome: "",
    baseCommissionPromotion: "",
    supportItems: [],
    supportItemsEtc: "",
    recruitmentRequirements: "",
    benefits: "",
    privacyAgreed: false,
};

export function PlannerRecruitApplyPage({ applicantType = "AGENCY_CEO" }) {
    const { accessToken } = useAuthContext();
    const location = useLocation();
    const navigate = useNavigate();
    const editMode = location.state?.editMode === true;
    const editPostId = location.state?.postId;
    const editInitialData = location.state?.initialData;
    // 수정 모드면 기존 글의 사람 유형을 우선한다 (라우트 prop보다 데이터가 정답).
    const resolvedApplicantType = editInitialData?.applicantType || applicantType;
    const isManager = resolvedApplicantType === "HR_MANAGER";

    const [form, setForm] = useState(() => {
        if (editMode && editInitialData) {
            const rawDetail = editInitialData.detailData;
            const d = (typeof rawDetail === "string" ? safeParse(rawDetail) : rawDetail) || {};
            return {
                contactName: editInitialData.contactName || "",
                contactPhone: formatPhone(editInitialData.contactPhone),
                companyName: editInitialData.companyName || "",
                companyAddress: editInitialData.companyAddress || "",
                companyAddressDetail: editInitialData.companyAddressDetail || "",
                recruitRegions: editInitialData.recruitRegions || [],
                strongSalesAreas: d.strongSalesAreas || [],
                strongSalesAreasEtc: d.strongSalesAreasEtc || "",
                relocationSettlementSupport: d.relocationSettlementSupport || "",
                relocationWorkType: d.relocationWorkType || "",
                employmentTypes: d.employmentTypes || [],
                salesBenefit: d.salesBenefit || "",
                educationSystem: d.educationSystem || "",
                averageIncome: d.averageIncome || "",
                baseCommissionPromotion: d.baseCommissionPromotion || "",
                supportItems: d.supportItems || [],
                supportItemsEtc: d.supportItemsEtc || "",
                recruitmentRequirements: d.recruitmentRequirements || "",
                benefits: d.benefits || "",
                privacyAgreed: true,
            };
        }
        return INITIAL_FORM;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [privacyTerms, setPrivacyTerms] = useState(null);
    const [showTermsModal, setShowTermsModal] = useState(false);

    useEffect(() => {
        termsRepository.getTermsByType("RECRUITMENT_PRIVACY")
            .then(setPrivacyTerms)
            .catch(() => {});
    }, []);

    const toggleInArray = (key, value) => {
        setForm((prev) => {
            const list = prev[key];
            const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
            return { ...prev, [key]: next };
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "contactPhone") {
            setForm((prev) => ({ ...prev, contactPhone: formatPhone(value) }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleAddressSearch = () => {
        const open = () => {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    setForm((prev) => ({ ...prev, companyAddress: data.roadAddress }));
                },
            }).open();
        };
        if (!window.daum?.Postcode) {
            const script = document.createElement("script");
            script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
            script.onload = open;
            document.head.appendChild(script);
        } else {
            open();
        }
    };

    const validate = () => {
        if (!form.contactName.trim()) return "담당자 성함을 입력해주세요.";
        if (!form.contactPhone.trim()) return "담당자 연락처를 입력해주세요.";
        if (!/^(02-\d{3,4}-\d{4}|0\d{1,2}-\d{3,4}-\d{4}|1[0-9]{3}-\d{4})$/.test(form.contactPhone)) return "연락처 형식이 올바르지 않습니다. (예: 010-1234-5678)";
        if (!form.companyName.trim()) return "회사명을 입력해주세요.";
        if (!form.companyAddress.trim()) return "주소를 검색해주세요.";
        if (form.strongSalesAreas.length === 0) return "강점 영업 영역을 1개 이상 선택해주세요.";
        if (!form.privacyAgreed) return "개인정보 수집·이용에 동의해주세요.";
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) { setError(validationError); return; }

        setLoading(true);
        setError(null);

        const trimOrUndef = (v) => (v && v.trim() ? v.trim() : undefined);

        const payload = {
            contactName: form.contactName.trim(),
            contactPhone: form.contactPhone.replace(/-/g, ""),
            applicantType: resolvedApplicantType,
            targetType: "PLANNER",
            jobCategoryCodes: ["INSURANCE_PLANNER"],
            companyName: form.companyName.trim(),
            companyAddress: form.companyAddress,
            companyAddressDetail: trimOrUndef(form.companyAddressDetail),
            recruitRegions: form.recruitRegions,
            detailData: {
                strongSalesAreas: form.strongSalesAreas,
                strongSalesAreasEtc: form.strongSalesAreas.includes("ETC")
                    ? trimOrUndef(form.strongSalesAreasEtc)
                    : undefined,
                relocationSettlementSupport: trimOrUndef(form.relocationSettlementSupport),
                relocationWorkType: form.relocationWorkType || undefined,
                employmentTypes: form.employmentTypes,
                salesBenefit: trimOrUndef(form.salesBenefit),
                educationSystem: trimOrUndef(form.educationSystem),
                averageIncome: trimOrUndef(form.averageIncome),
                baseCommissionPromotion: trimOrUndef(form.baseCommissionPromotion),
                supportItems: form.supportItems,
                supportItemsEtc: form.supportItems.includes("ETC")
                    ? trimOrUndef(form.supportItemsEtc)
                    : undefined,
                recruitmentRequirements: trimOrUndef(form.recruitmentRequirements),
                benefits: trimOrUndef(form.benefits),
            },
        };

        try {
            if (editMode) {
                await recruitmentRequestRepository.update(editPostId, payload, accessToken);
                navigate('/account?tab=my-recruit-posts', { replace: true });
            } else {
                await recruitmentRequestRepository.createRecruitmentRequest(payload, accessToken);
                setSuccess(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        } catch (err) {
            setError(err.message || "신청에 실패했습니다. 잠시 후 다시 시도해주세요.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="pra-page">
                <div className="pra-hero">
                    <div className="pra-hero-inner">
                        <p className="pra-hero-kicker">설계사 채용관 · 보험설계사 채용 · {isManager ? "채용 담당자" : "보험 대리점 대표"}</p>
                        <h1 className="pra-hero-title">{editMode ? '설계사 채용 등록 수정' : '설계사 채용 등록'}</h1>
                        <p className="pra-hero-sub">채용 지역과 지사 강점을 입력해주시면 설계사에게 노출됩니다. 구체적일수록 지원율이 높아집니다.</p>
                    </div>
                </div>

                <div className="pra-body">
                {success ? (
                    <div className="pra-success-card">
                        <div className="pra-success-badge">
                            <div className="pra-success-badge-ring" />
                            <svg className="pra-success-svg" viewBox="0 0 52 52" fill="none">
                                <circle className="pra-success-circle" cx="26" cy="26" r="23" stroke="#f16102" strokeWidth="2" fill="none" />
                                <polyline className="pra-success-check" points="14,27 22,35 38,18" stroke="#f16102" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                        </div>
                        <div className="pra-success-header">
                            <h2 className="pra-success-title">등록이 완료되었습니다!</h2>
                            <p className="pra-success-desc">설계사 채용 공고가 접수되었습니다.<br />담당자 검토 후 설계사 채용관에 노출됩니다.</p>
                        </div>
                        <div className="pra-success-summary">
                            <p className="pra-summary-title">접수 정보</p>
                            <ul className="pra-summary-list">
                                {form.contactName && <li className="pra-summary-item"><span className="pra-summary-key">담당자</span><span className="pra-summary-val">{form.contactName}</span></li>}
                                {form.contactPhone && <li className="pra-summary-item"><span className="pra-summary-key">연락처</span><span className="pra-summary-val">{form.contactPhone}</span></li>}
                                {form.companyName && <li className="pra-summary-item"><span className="pra-summary-key">회사명</span><span className="pra-summary-val">{form.companyName}</span></li>}
                                <li className="pra-summary-item"><span className="pra-summary-key">직무</span><span className="pra-summary-val">보험 설계사</span></li>
                            </ul>
                        </div>
                        <div className="pra-success-actions">
                            <a href="/premium/job-board" className="pra-success-home-btn">설계사 채용관으로</a>
                            <button className="pra-submit-btn pra-success-retry-btn" onClick={() => { setSuccess(false); setForm(INITIAL_FORM); }}>다시 등록하기</button>
                        </div>
                    </div>
                ) : (
                    <div className="pra-form-wrap">
                        <form className="pra-form" onSubmit={handleSubmit} noValidate>

                            {/* 담당자 정보 */}
                            <div className="pra-section-title">담당자 정보</div>
                            <div className="pra-row-2">
                                <div className="pra-field">
                                    <label className="pra-label">담당자 성함 <span className="pra-req">*</span></label>
                                    <input className="pra-input" type="text" name="contactName" value={form.contactName} onChange={handleChange} placeholder="성함을 입력하세요" />
                                </div>
                                <div className="pra-field">
                                    <label className="pra-label">담당자 연락처 <span className="pra-req">*</span></label>
                                    <input className="pra-input" type="tel" name="contactPhone" value={form.contactPhone} onChange={handleChange} placeholder="010-0000-0000" />
                                </div>
                            </div>

                            {/* 회사 정보 */}
                            <div className="pra-section-title" style={{ marginTop: "24px" }}>회사 정보</div>
                            <div className="pra-field">
                                <label className="pra-label">회사명 <span className="pra-req">*</span></label>
                                <input className="pra-input" type="text" name="companyName" value={form.companyName} onChange={handleChange} placeholder="회사명(지사명)을 입력하세요" />
                            </div>
                            <div className="pra-field">
                                <label className="pra-label">주소 <span className="pra-req">*</span></label>
                                <div className="pra-address-row">
                                    <input className="pra-input pra-address-input" type="text" value={form.companyAddress} readOnly placeholder="주소 검색을 클릭하세요" />
                                    <button type="button" className="pra-address-btn" onClick={handleAddressSearch}>주소 검색</button>
                                </div>
                            </div>
                            <div className="pra-field">
                                <label className="pra-label">주소 (상세)</label>
                                <input className="pra-input" type="text" name="companyAddressDetail" value={form.companyAddressDetail} onChange={handleChange} placeholder="상세주소를 입력하세요" />
                            </div>

                            {/* 채용 지역 (복수) */}
                            <div className="pra-section-title" style={{ marginTop: "24px" }}>채용 지역 <span className="pra-hint">(복수 선택)</span></div>
                            <div className="pra-region-grid">
                                {RECRUIT_REGIONS.map((r) => (
                                    <label key={r.code} className={`pra-checkbox-label ${form.recruitRegions.includes(r.code) ? "active" : ""}`}>
                                        <input type="checkbox" checked={form.recruitRegions.includes(r.code)} onChange={() => toggleInArray("recruitRegions", r.code)} />
                                        {r.label}
                                    </label>
                                ))}
                            </div>

                            {/* 강점 영업 영역 (복수, 필수) */}
                            <div className="pra-section-title" style={{ marginTop: "24px" }}>강점 영업 영역 <span className="pra-req">*</span> <span className="pra-hint">(복수 선택)</span></div>
                            <div className="pra-chip-wrap">
                                {STRONG_SALES_AREAS.map((a) => (
                                    <button
                                        type="button"
                                        key={a.code}
                                        className={`pra-chip ${form.strongSalesAreas.includes(a.code) ? "active" : ""}`}
                                        onClick={() => toggleInArray("strongSalesAreas", a.code)}
                                    >
                                        {a.label}
                                    </button>
                                ))}
                            </div>
                            {form.strongSalesAreas.includes("ETC") && (
                                <div className="pra-field" style={{ marginTop: "10px" }}>
                                    <input className="pra-input" type="text" name="strongSalesAreasEtc" value={form.strongSalesAreasEtc} onChange={handleChange} placeholder="기타 영업 영역을 입력하세요" />
                                </div>
                            )}

                            {/* 이직 시 제공 조건 */}
                            <div className="pra-section-title" style={{ marginTop: "24px" }}>이직 시 제공 조건</div>
                            <div className="pra-row-2">
                                <div className="pra-field">
                                    <label className="pra-label">정착지원금 또는 교육비 (금액·지급 조건)</label>
                                    <input className="pra-input" type="text" name="relocationSettlementSupport" value={form.relocationSettlementSupport} onChange={handleChange} placeholder="예: 정착지원금 최대 3,000만원, 12개월 분할 / 또는 교육비 지원" />
                                </div>
                                <div className="pra-field">
                                    <label className="pra-label">근무 형태</label>
                                    <select className="pra-input" name="relocationWorkType" value={form.relocationWorkType} onChange={handleChange}>
                                        <option value="">선택 안 함</option>
                                        {WORK_TYPES.map((w) => (
                                            <option key={w.code} value={w.code}>{w.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="pra-field" style={{ marginTop: "14px" }}>
                                <label className="pra-label">고용 형태 <span className="pra-hint">(복수 선택)</span></label>
                                <div className="pra-checkbox-grid">
                                    {EMPLOYMENT_TYPES.map((t) => (
                                        <label key={t.code} className={`pra-checkbox-label ${form.employmentTypes.includes(t.code) ? "active" : ""}`}>
                                            <input type="checkbox" checked={form.employmentTypes.includes(t.code)} onChange={() => toggleInArray("employmentTypes", t.code)} />
                                            {t.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="pra-row-2" style={{ marginTop: "14px" }}>
                                <div className="pra-field">
                                    <label className="pra-label">영업 베네핏</label>
                                    <input className="pra-input" type="text" name="salesBenefit" value={form.salesBenefit} onChange={handleChange} placeholder="예: 영업활동비 월 50만원, 차량 지원 등" />
                                </div>
                                <div className="pra-field">
                                    <label className="pra-label">교육 시스템</label>
                                    <input className="pra-input" type="text" name="educationSystem" value={form.educationSystem} onChange={handleChange} placeholder="예: 입사 후 3개월 집중 교육 프로그램 운영" />
                                </div>
                            </div>
                            <div className="pra-row-2" style={{ marginTop: "14px" }}>
                                <div className="pra-field">
                                    <label className="pra-label">평균 소득</label>
                                    <input className="pra-input" type="text" name="averageIncome" value={form.averageIncome} onChange={handleChange} placeholder="예: 월 평균 400만원" />
                                </div>
                                <div className="pra-field">
                                    <label className="pra-label">기본수수료 + 프로모션</label>
                                    <input className="pra-input" type="text" name="baseCommissionPromotion" value={form.baseCommissionPromotion} onChange={handleChange} placeholder="예: 기본수수료 80% + 신규 정착 프로모션 추가 지급" />
                                </div>
                            </div>

                            {/* 지원 사항 */}
                            <div className="pra-section-title" style={{ marginTop: "24px" }}>지원 사항 <span className="pra-hint">(복수 선택)</span></div>
                            <div className="pra-checkbox-grid">
                                {SUPPORT_ITEMS.map((s) => (
                                    <label key={s.code} className={`pra-checkbox-label ${form.supportItems.includes(s.code) ? "active" : ""}`}>
                                        <input type="checkbox" checked={form.supportItems.includes(s.code)} onChange={() => toggleInArray("supportItems", s.code)} />
                                        {s.label}
                                    </label>
                                ))}
                            </div>
                            {form.supportItems.includes("ETC") && (
                                <div className="pra-field" style={{ marginTop: "10px" }}>
                                    <input className="pra-input" type="text" name="supportItemsEtc" value={form.supportItemsEtc} onChange={handleChange} placeholder="기타 지원 사항을 입력하세요" />
                                </div>
                            )}
                            <div className="pra-field" style={{ marginTop: "10px" }}>
                                <label className="pra-label">채용요청사항</label>
                                <textarea className="pra-textarea" name="recruitmentRequirements" value={form.recruitmentRequirements} onChange={handleChange} placeholder="채용 시 요청하실 사항을 구체적으로 입력하세요" />
                            </div>
                            <div className="pra-field" style={{ marginTop: "10px" }}>
                                <label className="pra-label">복지사항</label>
                                <textarea className="pra-textarea" name="benefits" value={form.benefits} onChange={handleChange} placeholder="복지 사항을 구체적으로 입력하세요" />
                            </div>

                            {/* 개인정보 동의 */}
                            <div className="pra-privacy-row">
                                <label className="pra-privacy-check">
                                    <input type="checkbox" name="privacyAgreed" checked={form.privacyAgreed} onChange={handleChange} />
                                    <span>
                                        개인정보 수집·이용에 동의합니다.{" "}
                                        <button type="button" className="pra-terms-link" onClick={() => setShowTermsModal(true)}>
                                            내용보기
                                        </button>
                                    </span>
                                </label>
                            </div>

                            {error && <div className="pra-error">{error}</div>}

                            <button type="submit" className="pra-submit-btn" disabled={loading}>
                                {loading ? "등록 중..." : (editMode ? "수정 저장" : "채용 등록")}
                            </button>
                        </form>
                    </div>
                )}
                </div>
            </div>

            {showTermsModal && (
                <div className="pra-modal-overlay" onClick={() => setShowTermsModal(false)}>
                    <div className="pra-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pra-modal-header">
                            <h3>{privacyTerms?.title || "개인정보처리방침"}</h3>
                            <button className="pra-modal-close" onClick={() => setShowTermsModal(false)}>✕</button>
                        </div>
                        <div className="pra-modal-body">
                            {privacyTerms?.content
                                ? privacyTerms.content.split("\n").map((line, i) => <p key={i}>{line || <br />}</p>)
                                : <p>개인정보처리방침을 불러오는 중입니다.</p>
                            }
                        </div>
                        <div className="pra-modal-footer">
                            <button className="pra-submit-btn" onClick={() => { setForm((p) => ({ ...p, privacyAgreed: true })); setShowTermsModal(false); }}>
                                동의하고 닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />

            <style>{`
                .pra-page { padding-top: 80px; min-height: 100vh; background: #f7f4f1; }

                .pra-hero { background: linear-gradient(135deg, #fd7314 0%, #f1863f 100%); padding: 48px 20px; text-align: center; }
                .pra-hero-inner { max-width: 700px; margin: 0 auto; }
                .pra-hero-kicker { font-size: 12px; letter-spacing: 0.2em; color: rgba(255,255,255,0.8); font-weight: 600; margin: 0 0 8px; }
                .pra-hero-title { font-size: 2rem; font-weight: 800; color: #fff; margin: 0 0 10px; }
                .pra-hero-sub { font-size: 0.95rem; color: rgba(255,255,255,0.88); margin: 0; line-height: 1.6; }

                .pra-body { max-width: 800px; margin: 0 auto; padding: 48px 20px 80px; }

                .pra-form-wrap { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.07); padding: 36px 32px; }
                .pra-form { display: flex; flex-direction: column; gap: 14px; }

                .pra-section-title {
                    font-size: 0.8rem; font-weight: 700; letter-spacing: 0.08em;
                    text-transform: uppercase; color: #f16102;
                    padding-bottom: 8px; border-bottom: 1.5px solid #fde8d8;
                    margin-bottom: 2px; width: 100%; text-align: left;
                }
                .pra-hint { font-weight: 600; color: #b3a9a1; letter-spacing: 0; text-transform: none; }
                .pra-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .pra-field { display: flex; flex-direction: column; gap: 6px; }
                .pra-label { font-size: 0.85rem; font-weight: 600; color: #444; text-align: left; }
                .pra-req { color: #f16102; }

                .pra-input, .pra-textarea {
                    border: 1.5px solid #e5ddd7; border-radius: 8px;
                    padding: 10px 14px; font-size: 0.9rem; color: #333;
                    background: #fff; outline: none; transition: border-color 0.2s;
                    font-family: inherit;
                }
                .pra-input:focus, .pra-textarea:focus { border-color: #f16102; }
                .pra-textarea { resize: vertical; min-height: 90px; }

                .pra-address-row { display: flex; gap: 8px; }
                .pra-address-input { flex: 1; background: #faf7f4; cursor: default; }
                .pra-address-btn {
                    white-space: nowrap; padding: 10px 16px; background: #f16102;
                    color: #fff; border: none; border-radius: 8px; font-size: 0.85rem;
                    font-weight: 600; cursor: pointer; transition: background 0.2s;
                }
                .pra-address-btn:hover { background: #d95502; }

                .pra-region-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(84px, 1fr)); gap: 8px; }
                .pra-checkbox-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
                .pra-checkbox-label {
                    display: flex; align-items: center; justify-content: center; gap: 6px; padding: 9px 12px;
                    border: 1.5px solid #e5ddd7; border-radius: 8px; font-size: 0.85rem;
                    font-weight: 600; color: #666; cursor: pointer; transition: all 0.15s;
                }
                .pra-checkbox-label input { display: none; }
                .pra-checkbox-label.active { border-color: #f16102; color: #f16102; background: #fff8f4; }
                .pra-checkbox-label.active::before { content: "✓"; margin-right: 2px; }

                .pra-chip-wrap { display: flex; flex-wrap: wrap; gap: 8px; }
                .pra-chip {
                    padding: 8px 16px; border: 1.5px solid #e5ddd7; border-radius: 999px;
                    background: #fff; font-size: 0.85rem; font-weight: 600; color: #666;
                    cursor: pointer; transition: all 0.15s; font-family: inherit;
                }
                .pra-chip:hover { border-color: #f7a76c; }
                .pra-chip.active { border-color: #f16102; color: #fff; background: #f16102; }

                .pra-error { background: #fff2f2; border: 1px solid #ffc5c5; border-radius: 8px; padding: 12px 16px; font-size: 0.85rem; color: #d32f2f; }

                .pra-submit-btn {
                    margin-top: 8px; width: 100%; padding: 14px;
                    background: #f16102; color: #fff; border: none; border-radius: 10px;
                    font-size: 1rem; font-weight: 700; cursor: pointer; transition: background 0.2s, opacity 0.2s;
                }
                .pra-submit-btn:hover:not(:disabled) { background: #d95502; }
                .pra-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

                .pra-success-card {
                    max-width: 600px; margin: 0 auto; background: #fff;
                    border-radius: 24px; box-shadow: 0 8px 40px rgba(0,0,0,0.10);
                    padding: 56px 44px 48px; display: flex; flex-direction: column;
                    align-items: center; text-align: center; gap: 28px;
                    animation: pra-card-in 0.4s ease both;
                }
                @keyframes pra-card-in { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
                .pra-success-badge { position: relative; width: 96px; height: 96px; display: flex; align-items: center; justify-content: center; }
                .pra-success-badge-ring { position: absolute; inset: 0; border-radius: 50%; background: #fff5ee; animation: pra-ring-pulse 2s ease-in-out 0.8s infinite; }
                @keyframes pra-ring-pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.12);opacity:0.6} }
                .pra-success-svg { position: relative; width: 72px; height: 72px; }
                .pra-success-circle { stroke-dasharray: 145; stroke-dashoffset: 145; animation: pra-draw-circle 0.6s cubic-bezier(.4,0,.2,1) forwards; }
                .pra-success-check { stroke-dasharray: 48; stroke-dashoffset: 48; animation: pra-draw-check 0.35s ease 0.5s forwards; }
                @keyframes pra-draw-circle { to { stroke-dashoffset: 0; } }
                @keyframes pra-draw-check  { to { stroke-dashoffset: 0; } }
                .pra-success-header { display: flex; flex-direction: column; gap: 10px; }
                .pra-success-title { font-size: 1.55rem; font-weight: 800; color: #1c1814; margin: 0; }
                .pra-success-desc { font-size: 0.93rem; color: #888; line-height: 1.75; margin: 0; }
                .pra-success-summary { width: 100%; background: #faf7f4; border-radius: 14px; padding: 20px 24px; text-align: left; }
                .pra-summary-title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #f16102; margin: 0 0 14px; }
                .pra-summary-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
                .pra-summary-item { display: flex; align-items: baseline; gap: 12px; font-size: 0.88rem; }
                .pra-summary-key { flex-shrink: 0; width: 52px; color: #aaa; font-weight: 600; font-size: 0.82rem; }
                .pra-summary-val { color: #333; font-weight: 500; }
                .pra-success-actions { display: flex; gap: 12px; width: 100%; }
                .pra-success-home-btn { flex: 1; padding: 14px; border: 1.5px solid #e5ddd7; border-radius: 10px; font-size: 0.92rem; font-weight: 700; color: #666; background: #fff; text-decoration: none; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
                .pra-success-home-btn:hover { border-color: #f16102; color: #f16102; background: #fff8f4; }
                .pra-success-retry-btn { flex: 1; margin-top: 0; }

                .pra-privacy-row { margin-top: 8px; }
                .pra-privacy-check {
                    display: flex; align-items: flex-start; gap: 8px;
                    font-size: 0.85rem; color: #555; cursor: pointer;
                }
                .pra-privacy-check input { margin-top: 2px; accent-color: #f16102; flex-shrink: 0; }
                .pra-terms-link {
                    background: none; border: none; color: #f16102;
                    font-size: 0.85rem; font-weight: 600; cursor: pointer;
                    padding: 0; text-decoration: underline;
                }

                .pra-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
                    z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px;
                }
                .pra-modal {
                    background: #fff; border-radius: 14px; width: 100%;
                    max-width: 540px; max-height: 80vh; display: flex; flex-direction: column; overflow: hidden;
                }
                .pra-modal-header {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 20px 24px; border-bottom: 1px solid #f0ebe6;
                }
                .pra-modal-header h3 { margin: 0; font-size: 1rem; font-weight: 700; }
                .pra-modal-close {
                    background: none; border: none; font-size: 1.1rem;
                    cursor: pointer; color: #888; padding: 4px;
                }
                .pra-modal-body {
                    flex: 1; overflow-y: auto; padding: 20px 24px;
                    font-size: 0.85rem; color: #555; line-height: 1.8; text-align: left;
                }
                .pra-modal-body p { margin: 0 0 4px; }
                .pra-modal-footer { padding: 16px 24px; border-top: 1px solid #f0ebe6; }
                .pra-modal-footer .pra-submit-btn { margin-top: 0; }

                @media (max-width: 768px) {
                    .pra-hero { padding: 36px 16px; }
                    .pra-hero-title { font-size: 1.5rem; }
                    .pra-body { padding: 24px 16px 48px; }
                    .pra-form-wrap { padding: 24px 16px; }
                    .pra-row-2 { grid-template-columns: 1fr; }
                    .pra-input, .pra-textarea { font-size: 16px; }
                    .pra-success-card { padding: 36px 22px 32px; }
                    .pra-success-actions { flex-direction: column; }
                }

                @media (max-width: 480px) {
                    .pra-hero { padding: 28px 12px; }
                    .pra-hero-title { font-size: 1.25rem; }
                    .pra-hero-sub { font-size: 0.85rem; }
                    .pra-body { padding: 16px 12px 40px; }
                    .pra-form-wrap { padding: 20px 14px; border-radius: 12px; }
                    .pra-region-grid { grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); }
                }
            `}</style>
        </>
    );
}

function safeParse(str) {
    try { return JSON.parse(str); } catch { return {}; }
}
